/**
 * "Help me fill this" report drafting (plan Track 5 B6, 2026-09-30). Pure; no
 * React. Two halves:
 *
 *  - buildReportDraftPrompt(): the one message sent to the agent over the
 *    existing chat socket. It asks for a JSON draft ONLY and tells the agent
 *    not to call tools or stage/submit a report.
 *  - parseReportDraft(): strict parse of the agent's reply. The reply is
 *    hostile bytes: bounded, must be one plain JSON object, only whitelisted
 *    fields survive, each re-validated against the form's own rules. A field
 *    that fails is dropped (left blank for the person), never repaired.
 *
 * Nothing here submits anything; the draft only pre-fills the form, which
 * remains the sole submit path.
 */

/** Same list as ReportIncidentForm's category select. */
export const DRAFT_CATEGORIES = Object.freeze(['fire', 'smoke', 'weather', 'flood', 'earthquake', 'other']);

/** Bounds mirror the Backend IncidentReport model and the B3 proposal model. */
export const DRAFT_TITLE_MAX = 256;
export const DRAFT_DESCRIPTION_MAX = 2000;
export const DRAFT_LOCATION_MAX = 500;
/** Longest reply this parser will look at. */
export const DRAFT_REPLY_MAX_CHARS = 20000;

/** Marker the agent (and reviewers) can recognise the drafting request by. */
export const DRAFT_REQUEST_MARKER = '[report_draft_request]';

// C0 controls other than tab/LF/CR: the Backend rejects them in category and location.
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f￾￿]/g;

function cleanText(value, max) {
  if (typeof value !== 'string') return null;
  const t = value.trim();
  if (!t || t.length > max || CONTROL_RE.test(t)) return null;
  return t;
}

/**
 * @param {{ placeName?: string }|null} [clientLocation] - Location the person shared, if any.
 * @returns {string}
 */
export function buildReportDraftPrompt(clientLocation = null) {
  const place = cleanText(clientLocation?.placeName, 200);
  const where = place
    ? ` The person shared their location: ${place.replace(/[[\]\n\r]/g, ' ')}.`
    : '';
  return `${DRAFT_REQUEST_MARKER} Draft a community incident report from our conversation so far.${where} `
    + 'Rules: do NOT call any tools and do NOT submit or stage a report - the person reviews and submits it themselves. '
    + 'Use only details the person actually gave; never invent details, times, names or coordinates; '
    + 'use null for anything they did not state. '
    + 'Reply with ONLY one JSON object and no other text, with exactly these keys: '
    + `"title" (string, at most ${DRAFT_TITLE_MAX} characters, or null), `
    + `"category" (one of ${DRAFT_CATEGORIES.join(', ')}, or null), `
    + `"description" (string, at most ${DRAFT_DESCRIPTION_MAX} characters, or null), `
    + `"location" (a place or address the person named, at most ${DRAFT_LOCATION_MAX} characters, or null).`;
}

function unwrapJson(text) {
  const t = text.trim();
  const fence = t.match(/^```(?:json)?\s*\r?\n([\s\S]*?)\r?\n```$/i);
  return fence ? fence[1].trim() : t;
}

function isPlainObject(v) {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

/**
 * @param {string} reply - The agent's full reply text.
 * @returns {{ ok: true, draft: {title?: string, category?: string, description?: string, location?: string} }
 *   | { ok: false, reason: 'invalid'|'empty' }} Only fields that passed
 *   validation are present; `empty` = valid JSON but nothing usable.
 */
export function parseReportDraft(reply) {
  if (typeof reply !== 'string' || !reply.trim() || reply.length > DRAFT_REPLY_MAX_CHARS) {
    return { ok: false, reason: 'invalid' };
  }
  let parsed;
  try {
    parsed = JSON.parse(unwrapJson(reply));
  } catch {
    return { ok: false, reason: 'invalid' };
  }
  if (!isPlainObject(parsed)) return { ok: false, reason: 'invalid' };

  const draft = {};
  const title = cleanText(parsed.title, DRAFT_TITLE_MAX);
  if (title) draft.title = title;
  const category = typeof parsed.category === 'string' ? parsed.category.trim().toLowerCase() : null;
  if (category && DRAFT_CATEGORIES.includes(category)) draft.category = category;
  const description = cleanText(parsed.description, DRAFT_DESCRIPTION_MAX);
  if (description) draft.description = description;
  const location = cleanText(parsed.location, DRAFT_LOCATION_MAX);
  if (location) draft.location = location;

  return Object.keys(draft).length ? { ok: true, draft } : { ok: false, reason: 'empty' };
}
